<template>
  <div class="sales-list-wrapper m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header -->
    <v-card flat class="register-header-card pa-4 mb-4 rounded-lg">
      <!-- Row 1: Title and Actions -->
      <div class="d-flex flex-wrap align-center justify-space-between mb-3" style="gap: 12px;">
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-file-document-multiple-outline</v-icon>
          </div>
          <div>
            <div class="register-title">ESTIMATE &amp; SALES REGISTER</div>
            <div class="register-subtitle">Manage estimates, customer quotations &amp; bill settlements</div>
          </div>
        </div>

        <div class="header-actions-group d-flex flex-wrap align-center" style="gap: 8px;">
          <v-btn
            outlined
            color="#475569"
            class="header-nav-btn text-capitalize"
            @click="$router.push('/PurchaseList')"
          >
            <v-icon left small>mdi-history</v-icon>
            Purchase History
          </v-btn>
          <v-btn
            outlined
            color="#475569"
            class="header-nav-btn text-capitalize"
            @click="$router.push('/SalesPage1')"
          >
            <v-icon left small>mdi-receipt-text-outline</v-icon>
            Billing History
          </v-btn>
          <v-btn
            dark
            color="#9a6b27"
            class="header-action-btn text-capitalize elevation-1"
            @click="$router.push('/EstimatePage')"
          >
            <v-icon left small>mdi-plus</v-icon>
            New Estimate
          </v-btn>
        </div>
      </div>

      <v-divider class="my-3" style="border-color: #f1f5f9;"></v-divider>

      <!-- Row 2: Search Input & Record Count -->
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
        <div class="search-input-wrap">
          <v-text-field 
            v-model="keyword"
            clearable
            background-color="white"
            color="#9a6b27"
            hide-details
            placeholder="Search estimates by number, customer name, mobile..."
            outlined
            dense
            prepend-inner-icon="mdi-magnify"
            class="search-input"
          ></v-text-field>
        </div>

        <div class="d-flex align-center" style="gap: 8px;">
          <span class="count-badge" v-if="salesList">
            {{ salesList.length }} {{ salesList.length === 1 ? 'Record' : 'Records' }}
          </span>
          <v-btn
            icon
            small
            title="Refresh List"
            class="refresh-icon-btn"
            @click="getSales()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Master Data Table Card -->
    <v-card flat class="table-card rounded-lg overflow-hidden">
      <div v-if="salesList && salesList.length > 0">
        <v-simple-table class="pro-master-table m-stack-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 60px;">S.No.</th>
                <th class="text-left" style="width: 140px;">Estimate No</th>
                <th class="text-left" style="width: 130px;">Date</th>
                <th class="text-left">Customer Name</th>
                <th class="text-left" style="width: 140px;">Mobile Number</th>
                <th class="text-left" style="width: 120px;">Salesman</th>
                <th class="text-center" style="width: 140px;">Payment Status</th>
                <th class="text-center" style="width: 170px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, i) in salesList" :key="i" class="master-table-row">
                <td data-label="S.No." class="text-muted font-weight-medium">
                  <span v-if="page == 1">{{ i + 1 }}</span>
                  <span v-else>{{ i + 1 + 20 * (page - 1) }}</span>
                </td>
                <td data-label="Estimate No">
                  <span v-if="value._id.billNo" class="estimate-pill">
                    {{ (value._id.billPrefix || '') + value._id.billNo }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Date" class="date-cell">
                  <span v-if="value._id.create_date">{{ formatDate(value._id.create_date) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Customer Name" class="customer-cell font-weight-medium">
                  <span v-if="value._id.customerName">{{ value._id.customerName }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Mobile Number" class="mobile-cell">
                  <span v-if="value._id.mobileNumber">{{ value._id.mobileNumber }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Salesman">
                  <span v-if="value._id.staffCode" class="staff-code-pill">{{ value._id.staffCode }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Payment Status" class="text-center">
                  <span
                    :class="[
                      'status-pill',
                      value._id.paymentStatus === 'Paid' ? 'status-paid' :
                      value._id.paymentStatus === 'PartiallyPaid' ? 'status-partial' : 'status-pending'
                    ]"
                  >
                    <v-icon x-small class="mr-1" :color="value._id.paymentStatus === 'Paid' ? '#059669' : value._id.paymentStatus === 'PartiallyPaid' ? '#d97706' : '#64748b'">
                      {{ value._id.paymentStatus === 'Paid' ? 'mdi-check-circle' : value._id.paymentStatus === 'PartiallyPaid' ? 'mdi-clock-alert-outline' : 'mdi-clock-outline' }}
                    </v-icon>
                    {{ value._id.paymentStatus || 'Pending' }}
                  </span>
                </td>
                <td data-label="Actions" class="text-center">
                  <div class="d-flex align-center justify-center">
                    <v-btn 
                      dark 
                      color="#9a6b27"
                      class="settle-btn text-capitalize elevation-0 mr-2"
                      small 
                      @click="$router.push('/SalesSettlement?no=' + value._id._id)"
                    >
                      <v-icon left x-small>mdi-credit-card-check-outline</v-icon>
                      Settle
                    </v-btn>
                    <v-btn 
                      icon 
                      small 
                      class="edit-action-btn"
                      v-if="value._id.paymentStatus == 'Pending'" 
                      title="Edit Estimate"
                      @click="$router.push('/SalesSettlementEdit?no=' + value._id._id + '&&billno=' + value._id.billNo)"
                    >
                      <v-icon small color="#64748b">mdi-pencil-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Pagination Section -->
        <div class="d-flex justify-center align-center py-4 border-t" v-if="Pagelength > 1">
          <v-pagination
            small
            color="#9a6b27"
            v-model="page"
            :length="Pagelength"
          ></v-pagination>
        </div>
      </div>

      <!-- Clean Empty State -->
      <div v-else class="empty-state-wrapper py-12 text-center">
        <v-icon size="48" color="#94a3b8">mdi-file-search-outline</v-icon>
        <div class="empty-title mt-3">No estimates found</div>
        <div class="empty-subtitle">Create a new estimate or try adjusting your search filters.</div>
        <v-btn
          dark
          color="#9a6b27"
          class="mt-4 text-capitalize px-4 elevation-1"
          small
          @click="$router.push('/EstimatePage')"
        >
          <v-icon left small>mdi-plus</v-icon>
          Create First Estimate
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      page: 1,
      Pagelength: null,
      jewelleryId: null,
      salesList: [],
      keyword: "",
    };
  },
  mounted() {
    this.getSales();
  },
  watch: {
    keyword() {
      this.page = 1;
      this.getSales();
    },
    page() {
      this.getSales();
    },
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
  },
  methods: {
    getSales() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/estimate/getalllist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          limit: 20,
          keyword: this.keyword,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.salesList = response.data.data;
            this.Pagelength = Math.ceil(response.data.totalLength / 20);
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    formatDate(item) {
      if (!item) return "-";
      var dt = new Date(item);
      var day = dt.getDate();
      var year = dt.getFullYear();
      dt = dt.toString();
      return day + " " + dt.slice(4, 7) + " " + year;
    },
  },
};
</script>

<style scoped>
.sales-list-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  background: transparent;
}

.register-header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.register-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.register-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.header-nav-btn {
  border-color: #cbd5e1 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
}

.header-nav-btn:hover {
  background-color: #f8fafc !important;
  border-color: #94a3b8 !important;
}

.header-action-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.header-action-btn:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px) !important;
}

.search-input-wrap {
  flex: 1;
  max-width: 420px;
}

.search-input {
  width: 100%;
}

.search-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
}

.search-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.search-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.count-badge {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 4px 10px;
  border-radius: 6px;
}

.refresh-icon-btn {
  border: 1px solid #cbd5e1 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
}

.refresh-icon-btn:hover {
  background: #f8fafc !important;
}

.table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
}

.pro-master-table table {
  width: 100%;
  border-collapse: collapse;
}

.pro-master-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pro-master-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.master-table-row:hover {
  background-color: #f8fafc !important;
}

.estimate-pill {
  font-family: monospace;
  font-weight: 600;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: #1e293b;
}

.staff-code-pill {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
}

.status-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.status-paid {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.status-partial {
  background-color: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.status-pending {
  background-color: #f8fafc;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.settle-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 30px !important;
  box-shadow: 0 2px 8px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.settle-btn:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 4px 12px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1px) !important;
}

.edit-action-btn {
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
}

.edit-action-btn:hover {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
}

.text-muted {
  color: #94a3b8;
}

.border-t {
  border-top: 1px solid #f1f5f9;
}

.empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}
</style>
